import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, formatApiError } from '@/contexts/AuthContext';
import { auth as authApi } from '@/utils/api';
import { Eye, EyeOff, Zap, ArrowRight, Mail } from 'lucide-react';
import { toast } from 'sonner';

const BG_IMAGE = "https://images.unsplash.com/photo-1776704982763-b28e9301559d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyYWwlMjBnZW9tZXRyeXxlbnwwfHx8fDE3NzY4Mjk1ODR8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isRegister = location.pathname === '/register';

  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [magicSent, setMagicSent] = useState(false);
  const [magicSending, setMagicSending] = useState(false);
  const { register } = useAuth();

  const requestMagicLink = async () => {
    if (!email) return toast.error('Enter your email first');
    setMagicSending(true);
    try {
      await authApi.magicLinkRequest(email);
      setMagicSent(true);
      toast.success('Check your inbox for a sign-in link');
    } catch {
      // Still show success UI to avoid leaking existence
      setMagicSent(true);
    } finally { setMagicSending(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      let user;
      if (isRegister) {
        user = await register(email, password, name, company);
      } else {
        user = await login(email, password);
      }
      if (user?.role === 'super_admin') {
        navigate('/admin');
      } else {
        navigate('/portal');
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex font-sans" style={{ background: 'var(--bg)' }}>
      {/* Left panel — image + branding */}
      <div
        className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: '#0D1113' }}
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{ backgroundImage: `url(${BG_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#006080' }}>
              <Zap size={18} color="white" strokeWidth={2.5} />
            </div>
            <span className="text-white font-bold text-xl tracking-tight">Krris OS</span>
          </div>
        </div>
        <div className="relative z-10">
          <h2 className="text-4xl font-black text-white leading-tight mb-4">
            Your AI Chief-of-Staff.<br />Always on.
          </h2>
          <p className="text-base leading-relaxed" style={{ color: '#8A99A0' }}>
            Prospects become clients, scope is captured, projects run — all from one unified workspace.
          </p>
          <div className="mt-8 flex flex-col gap-3">
            {[
              'Multi-brand client portal',
              'AI-generated briefs & estimates',
              'Vault, bugs, chat, invoices — unified',
            ].map(f => (
              <div key={f} className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#006080' }} />
                <span className="text-sm" style={{ color: '#8A99A0' }}>{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-2 mb-8">
            <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: '#006080' }}>
              <Zap size={14} color="white" strokeWidth={2.5} />
            </div>
            <span className="font-bold text-lg" style={{ color: 'var(--ink)' }}>Krris OS</span>
          </div>

          <h1 className="text-3xl font-black mb-1" style={{ color: 'var(--ink)' }}>
            {isRegister ? 'Create your account' : 'Welcome back'}
          </h1>
          <p className="text-sm mb-8" style={{ color: 'var(--muted)' }}>
            {isRegister
              ? 'Join Krris OS and start your first project.'
              : 'Sign in to your Krris OS workspace.'}
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {isRegister && (
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Full Name</label>
                  <input
                    className="input-agency"
                    type="text"
                    placeholder="John Carter"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    required
                    data-testid="register-name-input"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Company</label>
                  <input
                    className="input-agency"
                    type="text"
                    placeholder="Acme Inc."
                    value={company}
                    onChange={e => setCompany(e.target.value)}
                    data-testid="register-company-input"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Email</label>
              <input
                className="input-agency"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Password</label>
                {!isRegister && (
                  <Link to="/forgot-password" className="text-xs font-semibold" style={{ color: '#006080' }} data-testid="forgot-password-link">
                    Forgot?
                  </Link>
                )}
              </div>
              <div className="relative">
                <input
                  className="input-agency pr-10"
                  type={showPass ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  onClick={() => setShowPass(!showPass)}
                  data-testid="toggle-password-visibility"
                >
                  {showPass ? <EyeOff size={16} color="var(--muted)" /> : <Eye size={16} color="var(--muted)" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="p-3 rounded-btn text-sm"
                style={{ background: 'rgba(179,74,58,0.1)', color: '#B34A3A', border: '1px solid rgba(179,74,58,0.2)' }}
                data-testid="auth-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex items-center justify-center gap-2 py-3 mt-1"
              data-testid="login-submit-button"
            >
              {loading ? (
                <span>Loading...</span>
              ) : (
                <>
                  {isRegister ? 'Create Account' : 'Sign In'}
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          {!isRegister && (
            <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
              {magicSent ? (
                <p className="text-xs text-center p-3 rounded-btn" style={{ background: '#3C8A5A15', color: '#3C8A5A' }} data-testid="magic-sent">
                  If that email exists, a sign-in link is on the way (expires in 15 min).
                </p>
              ) : (
                <button
                  type="button"
                  onClick={requestMagicLink}
                  disabled={magicSending || !email}
                  className="w-full flex items-center justify-center gap-2 py-2 text-sm rounded-btn font-semibold"
                  style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }}
                  data-testid="magic-link-button"
                >
                  <Mail size={14} /> {magicSending ? 'Sending…' : 'Email me a sign-in link'}
                </button>
              )}
            </div>
          )}

          <p className="text-sm text-center mt-6" style={{ color: 'var(--muted)' }}>
            {isRegister ? (
              <>Already have an account? <Link to="/login" className="font-semibold" style={{ color: '#006080' }} data-testid="login-link">Sign in</Link></>
            ) : (
              <>New to Krris OS? <Link to="/register" className="font-semibold" style={{ color: '#006080' }} data-testid="register-link">Create account</Link></>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
